<template>
	<div>
		<content-loader :message="$t('loading')" v-if="loading" />
		<div class="row mt-3" v-else-if="notFound">
			<div class="col-8 offset-2">
				<not-found />
			</div>
		</div>

		<page :title="pageTitle" tabs :sidebar-collapsed="collapsed" v-else>
			<template v-slot:headerTitle>
				<span>{{ device.device_type_name }} | </span>
				<span @click="goToParent" v-if="device.parent" class="pointer">{{ device.parent.name }}</span>
				<span v-if="device.parent"> > </span>
				{{ device.name }}
			</template>
			<template v-slot:badge>
				<device-status-badge :device="device" />
			</template>
			<template v-slot:itemDetail>
				<div class="mr-auto d-none d-xl-inline-block"> |
					<span class="ml-2"><small>Connection: <router-link class="text-white" :to="{ name: 'connection-view', params: { id: device.translator_connection.id } }">{{ device.translator_connection.display_name }}</router-link></small></span> |
					<span class="ml-2"><small>Last Heartbeat: <timestamp :data="device.last_heartbeat" /></small></span> |
					<span class="ml-2"><small>Last Sync: <timestamp :data="device.refreshed_at" /></small></span>
					<span v-if="deviceIsUserDeactivated"> | </span>
					<span class="ml-2" v-if="deviceIsUserDeactivated"><small>User Deactivated At: <timestamp :data="device.deactivated_at" /></small></span>
					<span v-if="deviceIsSystemDeactivated"> | </span>
					<span class="ml-2" v-if="deviceIsSystemDeactivated"><small>System Deactivated At: <timestamp :data="device.system_deactivated_at" /></small></span>
				</div>
				<div class="mr-auto d-xl-none">
					<b-dropdown class="text-white" variant="transparent" no-caret left>
						<template v-slot:button-content>
							<font-awesome-icon icon="info-circle" class="timestamp text-white" />
						</template>
						<b-list-group>
							<b-dd-header>
								Connection
							</b-dd-header>
							<b-dd-text>
								<router-link :to="{ name: 'connection-view', params: { id: device.translator_connection.id } }">{{ device.translator_connection.display_name }}</router-link>
							</b-dd-text>
							<b-dd-header>
								Last Heartbeat
							</b-dd-header>
							<b-dd-text>
								<timestamp :data="device.last_heartbeat" />
							</b-dd-text>
							<b-dd-header>
								Last Sync
							</b-dd-header>
							<b-dd-text>
								<timestamp :data="device.refreshed_at" />
							</b-dd-text>
							<b-dd-header v-if="deviceIsUserDeactivated">
								User Deactivated At
							</b-dd-header>
							<b-dd-text v-if="deviceIsUserDeactivated">
								<timestamp :data="device.deactivated_at" />
							</b-dd-text>
							<b-dd-header v-if="deviceIsSystemDeactivated">
								System Deactivated At
							</b-dd-header>
							<b-dd-text v-if="deviceIsSystemDeactivated">
								<timestamp :data="device.system_deactivated_at" />
							</b-dd-text>
							<b-dd-header>
								Device Type
							</b-dd-header>
							<b-dd-text class="text-nowrap">
								{{ device.device_type_name }}
							</b-dd-text>
						</b-list-group>
					</b-dropdown>
				</div>
				<div>
					<device-options-menu
						text-color="white"
						:device="device"
						:can-manage-device="canManageSettings"
						@sync="refreshDevice"
						@sync-screenshot="syncScreenshot"
						@deactivate="deactivateDevice"
						@activate="activateDevice"
						@reboot="rebootDevice"
					/>
				</div>
			</template>
			<page-tabs slot="fullwidth" :sidebar-collapsed="collapsed">
				<page-tab :to="deviceRoute('device-category', { categoryId: category.id })" v-for="(category, key) in categories" :key="key">
					{{ category.name }}
				</page-tab>
				<page-tab :to="deviceRoute('device-module', { moduleName: module.type })" v-for="(module, key) in device.modules" :key="key">
					{{ module.display }}
				</page-tab>
				<page-tab :to="deviceRoute('device')">Metrics</page-tab>
				<page-tab :to="deviceRoute('device-notifications')">
					Notifications
				</page-tab>
				<page-tab :to="deviceRoute('device-monitors')">
					Monitors ({{ device.monitor_count }})
				</page-tab>
				<page-tab :to="deviceRoute('device-info')">
					Additional Info
				</page-tab>
				<page-tab :to="deviceRoute('child-device', { typeId: type.id })" v-for="(type, key) in childDeviceTypes" :key="key">
					{{ pluralizeType(type.name) }}
				</page-tab>
			</page-tabs>
			<router-view @notFound="resourceNotFound" />
		</page>

		<!-- Outlet Number Modal for WattBox and similar devices -->
		<b-modal
			id="outlet-number-modal"
			ref="outletNumberModal"
			title="Reboot Device"
			@ok="confirmRebootWithOutlet"
			@cancel="cancelOutletInput"
			ok-title="Reboot"
			ok-variant="warning"
			cancel-variant="secondary"
		>
			<div v-if="linkedDevices && linkedDevices.length > 0" class="mb-3">
				<b-form-select v-model="outletNumber" :options="[{ value: null, text: 'Please select the outlet you wish to reboot:', disabled: true }, ...linkedDevices.map((device, index) => ({ value: device.outlet_number, text: `Outlet ${device.outlet_number}: ${device.device.display_name}` }))]" required />
			</div>
			<div v-else>
				<p>No device is linked to this wattbox</p>
			</div>
		</b-modal>
	</div>
</template>

<script>
import Page from '../../components/layout/Page';
import PageTabs from '../../components/PageTabs';
import PageTab from '../../components/PageTab';
import ContentLoader from '../../components/ContentLoader';
import ErrorModal from '../../mixins/ErrorModal';
import nprogress from 'nprogress';
import Timestamp from '../../components/Timestamp';
import NotFound from '../../components/errors/404Error';
import pluralize from 'pluralize';
import DeviceOptionsMenu from '../../components/devices/OptionsMenu';
import DeviceStatusBadge from '../../components/devices/StatusBadge';
import { OUTLET_REBOOT_DEVICE_TYPES } from '../../constants/device-types';

export default {
	components: {
		Page,
		PageTabs,
		PageTab,
		ContentLoader,
		Timestamp,
		NotFound,
		DeviceOptionsMenu,
		DeviceStatusBadge
	},

	data () {
		return {
			loading: true,
			notFound: false,
			outletNumber: null,
			deviceForReboot: null,
			linkedDevices: []
		};
	},

	inject: [ 'deviceRepository' ],

	mixins: [ ErrorModal ],

	watch: {
		$route (to, from) {
			if (parseInt(from.params.deviceId) !== parseInt(to.params.deviceId)) {
				if (!this.canView) {
					this.$router.push('/');
				}
				else {
					this.loadDevice(to.params.id, to.params.groupId, to.params.deviceId);
				}
			}
		}
	},

	mounted () {
		if (!this.canView) {
			this.$router.push('/');
		}
		else {
			this.loadDevice(this.clientId, this.groupId, this.deviceId);
		}
	},

	methods: {
		loadDevice (clientId, groupId, deviceId) {
			this.loading = true;
			this.notFound = false;
			this.$store.commit('sidebar/TOGGLE_NAVIGATION', false);
			if (this.device === null || this.device.id !== deviceId) {
				this.$store.dispatch('devices/refresh', { clientId, groupId, deviceId })
					.then(data => {
						// not handling data locally
					})
					.catch(e => {
						if (e.includes('No query results')) {
							this.notFound = true;
						}
						else {
							this.showErrorModal(e, 'Error Retrieving Device Information');
						}
					})
					.finally(() => {
						this.loading = false;
						this.$store.commit('sidebar/TOGGLE_NAVIGATION', true);
						nprogress.done();
					});
			}
		},
		reloadDevice () {
			this.$store.dispatch('devices/refresh', { clientId: this.clientId, groupId: this.groupId, deviceId: this.deviceId })
				.then(data => {
					// nothing
				})
				.catch(() => {
					// nothing
				});
		},
		deviceRoute (name, params = {}) {
			return {
				name,
				params: {
					...params,
					clientId: this.clientId,
					groupId: this.groupId,
					deviceId: this.deviceId
				}
			};
		},
		rebootDevice (device) {
			// Check if device type requires outlet number
			if (OUTLET_REBOOT_DEVICE_TYPES.includes(device.device_type_name)) {
				// Store device and show outlet input modal
				this.$store.dispatch('devices/getWattboxLinks', {
					clientId: this.clientId,
					groupId: this.groupId,
					deviceId: device.id
				})
					.then(links => {
						if (links && links.length > 0) {
							this.linkedDevices = links;
							this.deviceForReboot = device;
							this.outletNumber = null;
							this.$refs.outletNumberModal.show();
						}
						else {
							this.$bvModal.msgBoxOk('No outlet information found for this device, unable to reboot.', {
								title: 'Error',
								headerBgVariant: 'danger',
								okVariant: 'danger',
								headerTextVariant: 'white'
							});
						}
					});
			}
			else {
				// Show confirmation dialog for devices without outlet requirement
				this.$bvModal.msgBoxConfirm('Are you sure you want to reboot this device?', {
					title: 'Reboot Device',
					headerBgVariant: 'warning',
					headerTextVariant: 'white',
					okVariant: 'warning',
					cancelVariant: 'link'
				})
					.then(result => {
						if (result) {
							this.$store.dispatch('devices/rebootDevice', device.id)
								.then(data => {
									this.$bvModal.msgBoxOk('Successfully triggered device reboot.', {
										title: 'Success',
										headerBgVariant: 'success',
										okVariant: 'success',
										headerTextVariant: 'white'
									});
								})
								.catch(e => {
									this.showErrorModal(e, 'Error Rebooting Device');
								});
						}
					});
			}
		},
		confirmRebootWithOutlet () {
			if (!this.outletNumber || this.outletNumber < 1) {
				this.$bvModal.msgBoxOk('Please enter a valid outlet number.', {
					title: 'Invalid Outlet Number',
					headerBgVariant: 'danger',
					okVariant: 'danger',
					headerTextVariant: 'white'
				});
				return;
			}

			this.$store.dispatch('devices/rebootDevice', {
				id: this.deviceForReboot.id,
				outletNumber: this.outletNumber
			})
				.then(data => {
					this.$bvModal.msgBoxOk('Successfully triggered device reboot.', {
						title: 'Success',
						headerBgVariant: 'success',
						okVariant: 'success',
						headerTextVariant: 'white'
					});
				})
				.catch(e => {
					this.showErrorModal(e, 'Error Rebooting Device');
				});
		},
		cancelOutletInput () {
			this.deviceForReboot = null;
			this.outletNumber = null;
		},
		refreshDevice () {
			this.$store.dispatch('devices/refreshDevice', this.device.id)
				.then(data => {
					this.$bvModal.msgBoxOk('Device refresh is occurring in the background, and will be updated soon. This process can take up to 5 minutes.', {
						title: 'Success',
						headerBgVariant: 'success',
						okVariant: 'success',
						headerTextVariant: 'white'
					});
				})
				.catch(e => {
					this.showErrorModal(e, 'Error Triggering Device Refresh');
				});
		},
		deactivateDevice () {
			this.$bvModal.msgBoxConfirm('Are you sure you want to deactivate this device? This action will disable all monitors for this device.', {
				title: 'Deactivate Device',
				headerBgVariant: 'danger',
				headerTextVariant: 'white',
				okVariant: 'danger',
				cancelVariant: 'link'
			})
				.then(result => {
					if (result) {
						this.$store.dispatch('devices/deactivate', { id: this.device.id, group: true })
							.then(data => {
								this.reloadDevice();
								this.$bvModal.msgBoxOk('Device successfully deactivated.', {
									title: 'Success',
									headerBgVariant: 'success',
									okVariant: 'success',
									headerTextVariant: 'white'
								});
							})
							.catch(e => {
								this.showErrorModal(e, 'Error Deactivating Device');
							});
					}
				});
		},
		activateDevice () {
			this.$store.dispatch('devices/activate', { id: this.device.id, group: true })
				.then(data => {
					this.$bvModal.msgBoxOk('Device successfully activated.', {
						title: 'Success',
						headerBgVariant: 'success',
						okVariant: 'success',
						headerTextVariant: 'white'
					});

					this.reloadDevice();
				})
				.catch(e => {
					this.showErrorModal(e, 'Error Activating Device');
				});
		},
		resourceNotFound () {
			this.notFound = true;
		},
		isManageSlug (slug) {
			return slug.includes('manage_client') ||
				slug === 'super_admin';
		},
		pluralizeType (name) {
			return pluralize(name);
		},
		goToParent () {
			let params = { id: this.device.client_id, groupId: this.groupId, deviceId: this.device.parent.id };
			if (this.device.parent ?? this.device.parent.attribute_categories ?? this.device.parent.attribute_categories.count) {
				const category = this.device.parent.attribute_categories[0];
				params = {
					...params,
					categoryId: category.id
				};
			}

			this.$router.push({ name: this.$route.name, params: params });
		},
		/**
		 * make an api call to attempt to get a new screenshot
		 * for the device
		 *
		 * @return {void}
		 */
		syncScreenshot () {
			this.$store.dispatch('devices/refreshDeviceScreenshot', this.device.id)
				.then(data => {
					this.$bvModal.msgBoxOk('Screenshot sync is occurring in the background, and will be updated soon. This process can take up to 5 minutes.', {
						title: 'Success',
						headerBgVariant: 'success',
						okVariant: 'success',
						headerTextVariant: 'white'
					});
				})
				.catch(e => {
					this.showErrorModal(e, 'Error Triggering Device Refresh');
				});
		}
	},

	computed: {
		device () {
			return this.$store.getters['devices/current'];
		},

		collapsed () {
			return this.$store.getters['ui/appBarCollapsed'];
		},

		clientId () {
			return parseInt(this.$route.params.id);
		},

		groupId () {
			return parseInt(this.$route.params.groupId);
		},

		deviceId () {
			return parseInt(this.$route.params.deviceId);
		},

		categories () {
			if (this.device === null) {
				return [];
			}

			return this.device.device_attributes.reduce((carry, attribute) => {
				if (attribute.category !== null) {
					const categoryExists = carry.some(c => c && c.name === attribute.category.name);

					if (!categoryExists) {
						carry.push(attribute.category);
					}
				}

				return carry;
			}, []);
		},
		childDeviceTypes () {
			if (this.device === null) {
				return [];
			}

			return this.device.child_device_types.reduce((carry, type) => {
				carry.push(type);

				return carry;
			}, []);
		},
		canView () {
			return this.$store.getters['auth/hasPermission']('view_client', true, this.clientId) ||
				this.$store.getters['auth/hasPermission']('manage_client*', true, this.clientId);
		},
		canManageSettings () {
			return this.$store.getters['auth/hasPermission']('manage_client_device', true, this.clientId);
		},
		pageTitle () {
			let name = this.device.name;

			if (this.device.parent) {
				name = this.device.parent.name + ' > ' + name;
			}

			return name;
		},
		/**
		 * determine if the device has access to the screenshot module
		 *
		 * @returns {boolean}
		 */
		deviceHasScreenshotModule () {
			return this.device.modules.find(element => element.type === 'screenshot') !== undefined;
		},
		/**
		 * determine if the device has been deactivated
		 *
		 * @returns {boolean}
		 */
		deviceIsDeactivated () {
			return this.device.deactivated_at !== null || this.device.system_deactivated_at !== null;
		},
		/**
		 * determine if the device has been deactivated by User
		 *
		 * @returns {boolean}
		 */
		deviceIsUserDeactivated () {
			return this.device.deactivated_at !== null;
		},
		/**
		 * determine if the device has been deactivated by System
		 *
		 * @returns {boolean}
		 */
		deviceIsSystemDeactivated () {
			return this.device.system_deactivated_at !== null;
		}
	},
	destroyed () {
		this.$store.commit('devices/SET_CURRENT', null);
	}
};
</script>

<style scoped>
	.text-white {
		color: #FFFFFF;
	}
</style>
